import React, { useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import { round } from "lodash";

import Modal from "components/Modals";
import ActionButton from "components/UserCardList/components/UserCardResult/molecules/ActionButton";
import { AuthContext, MatchContext } from "context/providers";
import { CURRENCY_CONVERSION_CONFIG } from "lookup";
import {
  baseToTargetCurrency,
  loggedUserShouldINRConvert,
} from "utils/helpers/users";
import { cn } from "utils";

const getStandardMarkup = (desiredRate) => round(desiredRate * 1.5, 0);

const StandardMarkup = ({ desiredRate }) => {
  if (!desiredRate) return null;
  return (
    <span className="font-bold">
      {"$" + getStandardMarkup(desiredRate)}{" "}
      <span
        className="font-light"
        title="1.5 times the desired rate of freelancer"
      >
        ( 1.5x )
      </span>
    </span>
  );
};

const CalculatedMarkup = ({ desiredRate, currentRate }) => {
  if (!desiredRate) return null;
  const markup = ((currentRate - desiredRate) / currentRate) * 100;
  let markupPercent = round(markup, 2);
  if (!isFinite(markupPercent)) markupPercent = 0;
  return (
    <span className="font-bold">
      <span className={markupPercent > 0 ? "text-green-500" : "text-red-500"}>
        {markupPercent.toFixed(2)}%
      </span>
    </span>
  );
};

const FinalistModal = ({
  match,
  onClose,
  applicationId,
  jobOpportunityId,
  userId,
  matchParams,
  isUpdate,
}) => {
  const { updateMatch } = useContext(MatchContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const shouldINRConvert = loggedUserShouldINRConvert(loggedInUser);

  const initialDesiredRate = match?.rate?.value || 0;
  const initialInputNum = match?.customerRate?.value
    ? match.customerRate.value
    : getStandardMarkup(initialDesiredRate ?? 0);

  const [desiredRate, setDesiredRate] = useState(
    shouldINRConvert
      ? baseToTargetCurrency(
          initialDesiredRate,
          CURRENCY_CONVERSION_CONFIG.exchangeFactor,
          false
        ) || ""
      : initialDesiredRate || ""
  );
  const [inputNum, setInputNum] = useState(
    shouldINRConvert
      ? baseToTargetCurrency(
          initialInputNum || 0,
          CURRENCY_CONVERSION_CONFIG.exchangeFactor,
          false
        ) || ""
      : initialInputNum || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState({ success: "", error: "" });

  const usdValueRef = useRef(initialInputNum);
  const usdDesiredRateRef = useRef(initialDesiredRate);

  const submitIsDisabled =
    isLoading ||
    !inputNum ||
    Number(inputNum) <= 0 ||
    !desiredRate ||
    Number(desiredRate) <= 0;

  const handleSubmit = async () => {
    if (
      !usdDesiredRateRef.current ||
      usdDesiredRateRef.current <= 0 ||
      !usdValueRef.current ||
      usdValueRef.current <= 0
    ) {
      setMessages({
        success: "",
        error:
          "Desired rate and customer rate are required to mark as finalist",
      });
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        ...matchParams,
        customerRate: { value: Number(usdValueRef.current), currency: "USD" },
        rate: { value: Number(usdDesiredRateRef.current), currency: "USD" },
      };

      await updateMatch(applicationId, jobOpportunityId, params);
      onClose();
    } catch (error) {
      console.log("matchAction error: ", error);
      setMessages({ success: "", error: "Something went wrong" });
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isVisible={(applicationId || userId) && jobOpportunityId}
      title="Finalist"
      onClose={onClose}
      className="max-h-[90vh] border-2 overflow-x-hidden max-w-[450px] !w-[95%]"
    >
      <div className="flex flex-col gap-y-6">
        <div>
          <label
            htmlFor="desired-rate"
            className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2"
          >
            Talent Rate <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden shadow-sm w-fit">
            <span className="px-3 py-2 bg-slate-50 border-r border-slate-200 text-sm font-medium text-slate-500">
              {shouldINRConvert ? "INR" : "$"}
            </span>
            <input
              id="desired-rate"
              type="number"
              className="px-3 py-2 w-28 outline-none text-slate-900 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              value={desiredRate}
              min="0"
              onKeyDown={(e) => {
                if ([".", ",", "-", "+", "e", "E"].includes(e.key))
                  e.preventDefault();
              }}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (shouldINRConvert) {
                  const newConvertedValue =
                    CURRENCY_CONVERSION_CONFIG.exchangeFactor * (value || 0);
                  usdDesiredRateRef.current = parseInt(
                    Math.ceil(Number(newConvertedValue.toFixed(2)))
                  );
                } else {
                  usdDesiredRateRef.current = value;
                }
                setDesiredRate(value || "");
              }}
            />
            <span className="px-3 py-2 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">
              /hr
            </span>
          </div>
          {shouldINRConvert && (
            <span className="text-sm text-slate-400 mt-2 block">
              ={" "}
              <span className="font-semibold text-slate-900">
                ${usdDesiredRateRef.current} USD
              </span>
            </span>
          )}
        </div>

        <div>
          <label
            htmlFor="rate"
            className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2"
          >
            Customer Rate <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <span className="px-3 py-2 bg-slate-50 border-r border-slate-200 text-sm font-medium text-slate-500">
                {shouldINRConvert ? "INR" : "$"}
              </span>
              <input
                id="rate"
                type="number"
                className="px-3 py-2 w-28 outline-none text-slate-900 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={inputNum}
                min="0"
                onKeyDown={(e) => {
                  if ([".", ",", "-", "+", "e", "E"].includes(e.key))
                    e.preventDefault();
                }}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (shouldINRConvert) {
                    const newConvertedValue =
                      CURRENCY_CONVERSION_CONFIG.exchangeFactor * (value || 0);
                    usdValueRef.current = parseInt(
                      Math.ceil(Number(newConvertedValue.toFixed(2)))
                    );
                  } else {
                    usdValueRef.current = value;
                  }
                  setInputNum(value || "");
                  if (value > 0) setMessages({ success: "", error: "" });
                }}
              />
              <span className="px-3 py-2 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">
                /hr
              </span>
            </div>
            {shouldINRConvert && (
              <span className="text-sm text-slate-400">
                ={" "}
                <span className="font-semibold text-slate-900">
                  ${usdValueRef.current} USD
                </span>
              </span>
            )}
            {desiredRate && (
              <button
                type="button"
                className="text-xs text-sky-500 font-semibold hover:text-sky-700 transition-colors"
                onClick={() => {
                  const recommended = getStandardMarkup(desiredRate);
                  usdValueRef.current = recommended;
                  setInputNum(recommended);
                  setMessages({ success: "", error: "" });
                }}
              >
                Use recommended (${getStandardMarkup(desiredRate)}/hr)
              </button>
            )}
          </div>
        </div>

        {desiredRate && (
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                Rate Summary
              </p>
            </div>
            <div className="p-3 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">Desired Rate</span>
                <span className="font-bold text-slate-700">
                  ${desiredRate || "0"}
                  {shouldINRConvert &&
                    ` USD (${baseToTargetCurrency(
                      desiredRate || 0,
                      CURRENCY_CONVERSION_CONFIG.exchangeFactor
                    )} INR)`}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">
                  Standard w/ Markup
                </span>
                <StandardMarkup desiredRate={desiredRate} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">
                  Calculated Markup
                </span>
                <CalculatedMarkup
                  desiredRate={desiredRate}
                  currentRate={usdValueRef.current}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-slate-100">
          <div>
            {messages.success && (
              <span className="text-emerald-600 text-sm font-semibold">
                {messages.success}
              </span>
            )}
            {messages.error && (
              <span className="text-red-500 text-sm font-semibold">
                {messages.error}
              </span>
            )}
          </div>
          <ActionButton
            isDisabled={submitIsDisabled}
            isLoading={isLoading}
            action={() => handleSubmit()}
            className={cn("text-gray-600 border-b-2", {
              "pointer-events-none opacity-50": submitIsDisabled,
            })}
            title={
              !inputNum || Number(inputNum) <= 0
                ? "Desired rate and customer rate are required to mark as finalist"
                : isUpdate
                ? "Finalist"
                : ""
            }
            isUpdate={isUpdate}
            type="finalist"
            iconType="checkBadge"
            iconFill="transparent"
          />
        </div>
      </div>
    </Modal>
  );
};

FinalistModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default FinalistModal;
